import type {
  ChatSessionModel,
  ChatSessionParticipant,
  ChatSessionParticipantRole,
  ChatSessionParticipantsResponse,
  MessageVisibilityMode,
} from "@/api/models"

import { api } from "@/lib/api-client"
import { logger } from "@/lib/logger"

export const SessionSharingService = {
  async listSharedSessions(sessionType?: string): Promise<ChatSessionModel[]> {
    try {
      logger.debug("SessionSharingService.listSharedSessions", { sessionType })
      const sessions = (await api.get("/shared-sessions", {
        params: sessionType ? { session_type: sessionType } : undefined,
      })) as ChatSessionModel[]
      return sessions
    } catch (error) {
      logger.error("SessionSharingService.listSharedSessions: failed", error as Error)
      throw error
    }
  },

  async getSharedSession(sessionId: string): Promise<ChatSessionModel> {
    try {
      return (await api.get(`/sessions/${sessionId}/shared`)) as ChatSessionModel
    } catch (error) {
      logger.error("SessionSharingService.getSharedSession: failed", error as Error)
      throw error
    }
  },

  async listParticipants(sessionId: string): Promise<ChatSessionParticipantsResponse> {
    try {
      return (await api.get(
        `/sessions/${sessionId}/participants`
      )) as ChatSessionParticipantsResponse
    } catch (error) {
      logger.error("SessionSharingService.listParticipants: failed", error as Error)
      throw error
    }
  },

  async addParticipant(
    sessionId: string,
    participant: {
      oid: string
      displayName: string
      type: "user" | "group"
      role: ChatSessionParticipantRole
      messageAccessMode: MessageVisibilityMode
      sharedFromMessageId?: string
    }
  ): Promise<{ success: boolean; participant: ChatSessionParticipant }> {
    try {
      return (await api.post(`/sessions/${sessionId}/participants`, participant)) as {
        success: boolean
        participant: ChatSessionParticipant
      }
    } catch (error) {
      logger.error("SessionSharingService.addParticipant: failed", error as Error)
      throw error
    }
  },

  async markNotificationViewed(sessionId: string): Promise<{ success: boolean }> {
    try {
      return (await api.post(`/sessions/${sessionId}/notifications/viewed`)) as {
        success: boolean
      }
    } catch (error) {
      logger.error("SessionSharingService.markNotificationViewed: failed", error as Error)
      throw error
    }
  },

  async removeParticipant(
    sessionId: string,
    participantOid: string
  ): Promise<{ success: boolean }> {
    try {
      return (await api.delete(`/sessions/${sessionId}/participants/${participantOid}`)) as {
        success: boolean
      }
    } catch (error) {
      logger.error("SessionSharingService.removeParticipant: failed", error as Error)
      throw error
    }
  },

  async updateParticipantAccessMode(params: {
    sessionId: string
    participantOid: string
    messageAccessMode: MessageVisibilityMode
    sharedFromMessageId?: string
  }): Promise<{ success: boolean; participant: ChatSessionParticipant }> {
    try {
      return (await api.put(`/sessions/${params.sessionId}/participants/${params.participantOid}`, {
        messageAccessMode: params.messageAccessMode,
        sharedFromMessageId: params.sharedFromMessageId,
      })) as {
        success: boolean
        participant: ChatSessionParticipant
      }
    } catch (error) {
      logger.error("SessionSharingService.updateParticipantAccessMode: failed", error as Error)
      throw error
    }
  },

  async transferOwnership(
    sessionId: string,
    newOwnerOid: string
  ): Promise<{ success: boolean; newOwnerOid: string; message: string }> {
    try {
      return (await api.post(`/sessions/${sessionId}/transfer-ownership`, {
        newOwnerOid,
      })) as { success: boolean; newOwnerOid: string; message: string }
    } catch (error) {
      logger.error("SessionSharingService.transferOwnership: failed", error as Error)
      throw error
    }
  },

  async leaveSharedSession(
    sessionId: string
  ): Promise<{ success: boolean; action: "left" | "hidden" | "left_and_hidden" }> {
    try {
      return (await api.post(`/sessions/${sessionId}/leave`)) as {
        success: boolean
        action: "left" | "hidden" | "left_and_hidden"
      }
    } catch (error) {
      logger.error("SessionSharingService.leaveSharedSession: failed", error as Error)
      throw error
    }
  },
}
